'use client';

import { useContext, useMemo } from 'react';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { useSearchParams as useAppSearchParams } from 'next/navigation';
import { usePathname as useAppPathname, useRouter as useAppRouter } from '@/src/i18n/navigation';

type QueryValue = string | number | boolean | Array<string> | undefined;
type UrlObjectOrString = string | { pathname?: string; query?: Record<string, QueryValue> };

type TransitionOptions = { shallow?: boolean; scroll?: boolean };

type RouterEvents = {
  on: (event: string, handler: (...args: Array<unknown>) => void) => void;
  off: (event: string, handler: (...args: Array<unknown>) => void) => void;
  emit?: (event: string, ...args: Array<unknown>) => void;
};

type PagesRouter = {
  push: (url: UrlObjectOrString, as?: string, options?: TransitionOptions) => Promise<boolean> | boolean | void;
  replace: (url: UrlObjectOrString, as?: string, options?: TransitionOptions) => Promise<boolean> | boolean | void;
  prefetch: (url: string) => Promise<void> | void;
  back: () => void;
  beforePopState: (callback: () => boolean) => void;
  pathname: string;
  query: Record<string, string | Array<string> | undefined>;
  asPath?: string;
  events?: RouterEvents;
};

export type CompatRouter = {
  push: (url: UrlObjectOrString, as?: string, options?: TransitionOptions) => void;
  replace: (url: UrlObjectOrString, as?: string, options?: TransitionOptions) => void;
  prefetch: (url: string) => void;
  back: () => void;
  beforePopState: (callback: () => boolean) => void;
  pathname: string;
  query: Record<string, string | Array<string> | undefined>;
  asPath: string;
  events?: RouterEvents;
};

function toHref(url: UrlObjectOrString): string {
  if (typeof url === 'string') return url;
  const pathname = url.pathname ?? '/';
  const query = url.query ?? {};
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        params.append(k, item);
      }
    } else {
      params.set(k, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/**
 * Router hook that works in either App Router or Pages Router trees. It reads
 * both router contexts unconditionally (keeping hook order stable) and returns
 * whichever is live. Uses RouterContext directly for Pages so it returns null
 * instead of throwing when mounted inside App Router.
 */
export function useCompatRouter(): CompatRouter {
  const appRouter = useAppRouter();
  const appPathname = useAppPathname();
  const appSearchParams = useAppSearchParams();
  const pagesRouter = useContext(RouterContext) as PagesRouter | null;

  const inAppRouter = appPathname !== null;

  return useMemo<CompatRouter>(() => {
    if (inAppRouter && appPathname !== null) {
      const queryRecord: Record<string, string | Array<string> | undefined> = {};
      appSearchParams?.forEach((value, key) => {
        const existing = queryRecord[key];
        if (existing === undefined) {
          queryRecord[key] = value;
        } else if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          queryRecord[key] = [existing, value];
        }
      });
      const qs = appSearchParams?.toString() ?? '';
      const asPath = qs ? `${appPathname}?${qs}` : appPathname;

      return {
        push: (url, _as, options) => appRouter.push(toHref(url), { scroll: options?.scroll ?? true }),
        replace: (url, _as, options) => appRouter.replace(toHref(url), { scroll: options?.scroll ?? true }),
        prefetch: (url) => appRouter.prefetch(url),
        back: () => appRouter.back(),
        beforePopState: () => undefined,
        pathname: appPathname,
        query: queryRecord,
        asPath,
      };
    }

    if (pagesRouter) {
      const pagesPathname = (pagesRouter.asPath ?? '/').split('?')[0] ?? '/';
      return {
        push: (url, as, options) => {
          void pagesRouter.push(url, as, options);
        },
        replace: (url, as, options) => {
          void pagesRouter.replace(url, as, options);
        },
        prefetch: (url) => {
          void pagesRouter.prefetch(url);
        },
        back: () => pagesRouter.back(),
        beforePopState: (callback) => pagesRouter.beforePopState(callback),
        pathname: pagesPathname,
        query: pagesRouter.query,
        asPath: pagesRouter.asPath ?? '/',
        events: pagesRouter.events,
      };
    }

    const noop = () => {};
    return {
      push: noop,
      replace: noop,
      prefetch: noop,
      back: noop,
      beforePopState: noop,
      pathname: '/',
      query: {},
      asPath: '/',
    };
  }, [inAppRouter, appRouter, appPathname, appSearchParams, pagesRouter]);
}
